/**
 * What a connected Google / Microsoft account actually does in WeaveIn
 * (TRUS-638). Replaces the old "Sync Settings" switches, which were hard-coded
 * defaults that were never read or saved — so "Email: off" was shown while the
 * Mailbox read that account's mail.
 *
 * Everything here is real state:
 *  - capabilities come from the scopes the user granted (/api/oauth/status);
 *  - calendar sync is the user's calendar_sync_configs row, and "Sync now" pulls
 *    events (/api/calendar/sync/pull/{provider});
 *  - cloud files (Drive / OneDrive & SharePoint) are per user AND per org, so
 *    the row names the org it applies to.
 */

import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, Check, Clock, Loader2, Mail, RefreshCw, Users, Video, X } from 'lucide-react';
import { toast } from 'sonner';
import { calendarSyncAPI, ConnectionCapabilities } from '@/services/api/oauth';
import { cloudFilesApi, CloudConnectProvider } from '@/services/cloudFilesApi';
import { ProviderMark } from '@/components/messaging/cloud/CloudIcons';
import { CONNECT_PROVIDER_LABEL, useCloudPolicyMessages } from '@/components/messaging/cloud/connectErrors';
import { cloudStatusKey, useCloudConnect, useCloudFilesStatus } from '@/components/messaging/cloud/useCloudConnect';
import useActiveOrgId from '@/hooks/useActiveOrgId';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';

type Provider = 'google' | 'microsoft';

export const CALENDAR_SYNC_KEY = ['calendar-sync-configs'] as const;

function StateBadge({ on, onLabel, offLabel, testId }: { on: boolean; onLabel: string; offLabel: string; testId?: string }) {
  return on ? (
    <span
      data-testid={testId}
      data-state="on"
      className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300"
    >
      <Check className="h-3 w-3" aria-hidden="true" />
      {onLabel}
    </span>
  ) : (
    <span
      data-testid={testId}
      data-state="off"
      className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300"
    >
      <X className="h-3 w-3" aria-hidden="true" />
      {offLabel}
    </span>
  );
}

function Row({ icon, title, hint, right, testId }: { icon: React.ReactNode; title: string; hint?: React.ReactNode; right: React.ReactNode; testId?: string }) {
  return (
    <li
      data-testid={testId}
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-600 dark:bg-gray-800"
    >
      <div className="flex min-w-0 items-center gap-3">
        <div className="rounded-lg bg-gray-100 p-2 text-gray-600 dark:bg-gray-700 dark:text-gray-300">{icon}</div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white">{title}</p>
          {hint && <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2">{right}</div>
    </li>
  );
}

interface ConnectionDetailsProps {
  provider: Provider;
  capabilities: ConnectionCapabilities | null | undefined;
  onReconnect: () => void;
  /** Google only: Gmail is asked for separately from calendar/drive (it is a
   *  restricted scope), so a missing mailbox gets its own button. */
  onEnableMailbox?: () => void;
}

export function ConnectionDetails({ provider, capabilities, onReconnect, onEnableMailbox }: ConnectionDetailsProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const queryClient = useQueryClient();
  const known = !!capabilities;
  const caps = capabilities ?? {};
  const on = t('settings.connections.on', 'On');
  const notGranted = t('settings.connections.not_granted', 'Not allowed');

  const syncConfigs = useQuery({
    queryKey: CALENDAR_SYNC_KEY,
    queryFn: calendarSyncAPI.getConfigs,
    retry: false,
    staleTime: 60_000,
  });
  // TRUS-694: the badge is the sync's real state, not the grant. "On" only when
  // a config exists, is enabled and its last run worked.
  const syncConfig = syncConfigs.data?.find((c) => c.provider === provider);
  const syncState = syncConfig?.state ?? (syncConfig ? (syncConfig.is_active ? 'ok' : 'failing') : undefined);

  const pull = useMutation({
    mutationFn: () => calendarSyncAPI.pull(provider),
    onSuccess: (r) => {
      toast.success(r?.message || t('settings.connections.calendar_synced', 'Calendar synced'));
      void queryClient.invalidateQueries({ queryKey: CALENDAR_SYNC_KEY });
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.detail || t('settings.connections.calendar_sync_failed', 'Could not sync the calendar'));
    },
  });

  const calendarHint =
    syncState === 'failing'
      ? t(
          'settings.connections.calendar_failing',
          'Sync is not working ({{error}}). Reconnect to fix it.',
          { error: syncConfig?.last_sync_error || t('settings.connections.calendar_failing_unknown', 'unknown error') },
        )
      : syncState === 'off'
        ? t('settings.connections.calendar_off', 'Sync is turned off for this account')
        : syncConfig?.last_sync_at
          ? t('settings.connections.calendar_last_synced', 'Last synced {{when}}', { when: fmt.dateTime(syncConfig.last_sync_at) })
          : syncConfig
            ? t('settings.connections.calendar_not_synced_yet', 'Not synced yet')
            : t('settings.connections.calendar_hint_two_way', 'Your WeaveIn events are copied to this calendar; Sync now brings its events in');

  return (
    <div
      className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-700/50"
      data-testid={`connection-details-${provider}`}
    >
      <h5 className="mb-1 font-semibold text-gray-900 dark:text-white">
        {t('settings.connections.what_it_does', 'What this connection does')}
      </h5>
      <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
        {t('settings.connections.applies_to_all_orgs', 'Applies to all your organizations.')}
      </p>

      {!known && (
        <div
          className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200"
          data-testid={`capabilities-unknown-${provider}`}
        >
          <span>
            {t(
              'settings.connections.unknown',
              "This connection was made before WeaveIn recorded what it can access. Reconnect once to see it here.",
            )}
          </span>
          <button type="button" onClick={onReconnect} className="font-medium underline">
            {t('settings.connections.reconnect', 'Reconnect')}
          </button>
        </div>
      )}

      <ul className="space-y-2">
        {known && (
          <Row
            testId={`cap-mailbox-${provider}`}
            icon={<Mail className="h-4 w-4" />}
            title={t('settings.connections.mailbox', 'Mailbox')}
            hint={
              caps.mailbox
                ? caps.send_mail
                  ? t('settings.connections.mailbox_hint_send', 'Read and send email from the Inbox → Mailbox tab')
                  : t('settings.connections.mailbox_hint_read', 'Read email in the Inbox → Mailbox tab')
                : onEnableMailbox
                  ? t(
                      'settings.connections.mailbox_enable_hint',
                      'Bring your email with clients into WeaveIn. Google may show a security notice while it reviews WeaveIn; choose Advanced, then continue.',
                    )
                  : t('settings.connections.reconnect_to_add', 'Reconnect and allow access to add this')
            }
            right={
              !caps.mailbox && onEnableMailbox ? (
                <button
                  type="button"
                  onClick={onEnableMailbox}
                  data-testid={`enable-mailbox-${provider}`}
                  className="inline-flex items-center gap-1 rounded-lg bg-blue-100 px-3 py-1.5 text-xs text-blue-700 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-300"
                >
                  <Mail className="h-3 w-3" />
                  {t('settings.connections.mailbox_enable', 'Turn on email sync')}
                </button>
              ) : (
                <StateBadge on={!!caps.mailbox} onLabel={on} offLabel={notGranted} />
              )
            }
          />
        )}

        <Row
          testId={`cap-calendar-${provider}`}
          icon={<Calendar className="h-4 w-4" />}
          title={t('settings.connections.calendar', 'Calendar')}
          hint={
            known && !caps.calendar ? (
              t('settings.connections.reconnect_to_add', 'Reconnect and allow access to add this')
            ) : (
              <span className="inline-flex items-center gap-1">
                {syncConfig?.last_sync_at && <Clock className="h-3 w-3" aria-hidden="true" />}
                {calendarHint}
              </span>
            )
          }
          right={
            <>
              {known && !caps.calendar && <StateBadge on={false} onLabel={on} offLabel={notGranted} />}
              {(!known || caps.calendar) && (
                <StateBadge
                  testId={`calendar-sync-state-${provider}`}
                  on={syncState === 'ok'}
                  onLabel={on}
                  offLabel={
                    syncState === 'failing'
                      ? t('settings.connections.calendar_state_failing', 'Not working')
                      : syncState === 'off'
                        ? t('settings.connections.calendar_state_off', 'Off')
                        : t('settings.connections.calendar_not_synced_yet', 'Not synced yet')
                  }
                />
              )}
              {syncState === 'failing' && (
                <button type="button" onClick={onReconnect} className="text-xs font-medium underline text-amber-700 dark:text-amber-300">
                  {t('settings.connections.reconnect', 'Reconnect')}
                </button>
              )}
              {(!known || caps.calendar) && (
                <button
                  type="button"
                  onClick={() => pull.mutate()}
                  disabled={pull.isPending}
                  data-testid={`calendar-sync-now-${provider}`}
                  className="inline-flex items-center gap-1 rounded-lg bg-blue-100 px-3 py-1.5 text-xs text-blue-700 hover:bg-blue-200 disabled:opacity-50 dark:bg-blue-900/40 dark:text-blue-300"
                >
                  {pull.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  {t('settings.connections.sync_now', 'Sync now')}
                </button>
              )}
            </>
          }
        />

        {/* WeaveIn no longer asks Google for contacts (nothing reads them). */}
        {known && provider !== 'google' && (
          <Row
            testId={`cap-contacts-${provider}`}
            icon={<Users className="h-4 w-4" />}
            title={t('settings.connections.contacts', 'Contacts')}
            hint={
              caps.contacts
                ? t('settings.connections.contacts_hint', 'Allowed — used when you import contacts')
                : t('settings.connections.reconnect_to_add', 'Reconnect and allow access to add this')
            }
            right={<StateBadge on={!!caps.contacts} onLabel={on} offLabel={notGranted} />}
          />
        )}

        {known && provider === 'microsoft' && (
          <Row
            testId="cap-meetings-microsoft"
            icon={<Video className="h-4 w-4" />}
            title={t('settings.connections.teams_meetings', 'Teams meetings')}
            right={<StateBadge on={!!caps.meetings} onLabel={on} offLabel={notGranted} />}
          />
        )}

        <CloudFilesRow provider={provider === 'google' ? 'google_drive' : 'microsoft_files'} />
      </ul>
    </div>
  );
}

/** Drive / OneDrive & SharePoint for message attachments — per user AND per org. */
export function CloudFilesRow({ provider }: { provider: CloudConnectProvider }) {
  const { t } = useAppTranslation();
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  const status = useCloudFilesStatus();
  const { connect, connecting } = useCloudConnect();
  const { orgName } = useCloudPolicyMessages();
  const st = status.data?.[provider];
  const label = CONNECT_PROVIDER_LABEL[provider];

  const disconnect = useMutation({
    mutationFn: () => cloudFilesApi.disconnect(provider),
    onSuccess: () => {
      toast.success(t('messaging.cloud.disconnected', '{{provider}} disconnected', { provider: label }));
      void queryClient.invalidateQueries({ queryKey: cloudStatusKey(orgId) });
    },
    onError: () => {
      toast.error(t('messaging.cloud.disconnectFailed', 'Could not disconnect {{provider}}', { provider: label }));
    },
  });

  let hint: string;
  let right: React.ReactNode = null;
  if (status.isLoading) {
    hint = t('settings.connections.checking', 'Checking…');
  } else if (!st) {
    hint = t('messaging.cloud.statusFailed', 'Could not check your cloud file connections');
  } else if (!st.org_enabled) {
    hint = t('settings.connections.files_org_off', "{{org}} hasn't turned this on. An org admin can turn it on under Cloud files below.", {
      org: orgName,
    });
  } else if (!st.available) {
    hint = t('messaging.cloud.notConfiguredShort', 'Not configured for this workspace');
  } else if (st.connected) {
    hint = t('settings.connections.files_connected', 'Connected as {{email}} · {{org}} only', {
      email: st.account_email || '—',
      org: orgName,
    });
    right = (
      <button
        type="button"
        onClick={() => disconnect.mutate()}
        disabled={disconnect.isPending}
        className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200"
      >
        {disconnect.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
        {t('messaging.cloud.disconnect', 'Disconnect')}
      </button>
    );
  } else {
    hint = t('settings.connections.files_not_connected', 'Attach files to messages · {{org}} only · separate sign-in', { org: orgName });
    right = (
      <button
        type="button"
        onClick={() => void connect(provider, st.connect_path)}
        disabled={connecting === provider}
        className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
      >
        {connecting === provider && <Loader2 className="h-3 w-3 animate-spin" />}
        {t('messaging.cloud.connect', 'Connect')}
      </button>
    );
  }

  return (
    <Row
      testId={`cap-files-${provider}`}
      icon={<ProviderMark provider={provider === 'google_drive' ? 'google_drive' : 'onedrive'} className="h-4 w-4" />}
      title={label}
      hint={hint}
      right={
        <>
          {st?.org_enabled && st.available && (
            <StateBadge
              on={!!st.connected}
              onLabel={t('settings.connections.connected', 'Connected')}
              offLabel={t('messaging.cloud.notConnected', 'Not connected')}
            />
          )}
          {right}
        </>
      }
    />
  );
}

export default ConnectionDetails;
